import React from "react";
import { Package, Calendar, DollarSign, User, MapPin } from "lucide-react";
import { formatCurrency, formatDate, formatDateTime, formatKey, formatValue } from "../utils/responseParser";

/**
 * GenericCard - A flexible card component for displaying any entity type.
 * Uses backend-provided configuration to determine layout and styling.
 *
 * @param {Object} props
 * @param {Object} props.data - The entity data object
 * @param {Object} props.config - The CardConfig from backend metadata
 * @returns {JSX.Element}
 */
const GenericCard = ({ data, config }) => {
    if (!data || !config) return null;

    const titleValue = getFieldValue(data, config.titleField);
    const subtitleValue = getFieldValue(data, config.subtitleField);
    const Icon = getIcon(config.iconType);
    const statusConfig = config.statusConfig;
    const statusValue = statusConfig ? getFieldValue(data, statusConfig.field) : null;
    const statusColor = statusValue && statusConfig?.colorMap
        ? statusConfig.colorMap[statusValue] || "bg-gray-100 text-gray-700"
        : "bg-gray-100 text-gray-700";

    return (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <Icon className="w-4 h-4 text-acs-primary-green" />
                <span className="text-sm font-semibold text-gray-800 flex-1">
                    {titleValue || "Details"}
                </span>
                {statusValue && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor}`}>
                        {statusValue}
                    </span>
                )}
            </div>

            {/* Body */}
            <div className="p-3">
                {subtitleValue && (
                    <div className="text-sm font-medium text-gray-600 mb-3">{subtitleValue}</div>
                )}

                {config.sections?.map((section, idx) => (
                    <div
                        key={idx}
                        className={idx > 0 ? "mt-3 pt-3 border-t border-gray-100" : ""}
                    >
                        {section.label && (
                            <span className="text-[10px] text-gray-500 uppercase tracking-wide block mb-2">
                                {section.label}
                            </span>
                        )}
                        <div className="space-y-1.5">
                            {section.fields.map((fieldKey, fieldIdx) => {
                                const fieldValue = getFieldValue(data, fieldKey);
                                if (fieldValue === null || fieldValue === undefined) return null;

                                // Smart formatting based on field name
                                const formattedValue = formatFieldValue(fieldKey, fieldValue);

                                return (
                                    <div key={fieldIdx} className="flex justify-between text-xs">
                                        <span className="text-gray-500">{formatKey(fieldKey)}</span>
                                        <span className="font-medium text-gray-800">{formattedValue}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Get field value from data, supports array of fallback keys.
 */
const getFieldValue = (data, field) => {
    if (!field) return null;
    if (Array.isArray(field)) {
        for (const f of field) {
            const val = data?.[f];
            if (val !== undefined && val !== null) return val;
        }
        return null;
    }
    return data?.[field];
};

/**
 * Get icon component by type.
 */
const getIcon = (iconType) => {
    const icons = {
        package: Package,
        calendar: Calendar,
        dollar: DollarSign,
        user: User,
        mappin: MapPin
    };
    return icons[iconType] || Package;
};

/**
 * Smart field value formatting based on field name and type.
 */
const formatFieldValue = (fieldName, value) => {
    if (value == null) return "-";

    // Format currency
    const keyLower = String(fieldName).toLowerCase();
    if (keyLower.includes("amount") || keyLower.includes("price") ||
        keyLower.includes("cost") || keyLower.includes("charge") ||
        keyLower.includes("total")) {
        if (typeof value === "number") {
            return formatCurrency(value);
        }
    }

    // Format dates
    if (keyLower.includes("date") || keyLower.includes("time")) {
        if (typeof value === "string" || value instanceof Date) {
            const formatted = keyLower.includes("time") ? formatDateTime(value) : formatDate(value);
            if (formatted !== "-") return formatted;
        }
    }

    // Default formatting
    return formatValue(value);
};

export default GenericCard;
